import React, { useState, useEffect, useRef } from 'react';
import api from '../api';

function VerificationCenter() {
  const [kyc, setKyc] = useState(null);
  const [form, setForm] = useState({
    fullName: '',
    documentType: 'Passport',
    documentNumber: '',
  });
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [selfieImage, setSelfieImage] = useState(null);
  const [frontPreview, setFrontPreview] = useState('');
  const [backPreview, setBackPreview] = useState('');
  const [selfiePreview, setSelfiePreview] = useState('');
  const [message, setMessage] = useState('');

  // Camera modal state
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    api.get('/api/kyc/status').then(res => setKyc(res.data));
  }, []);

  // Cleanup camera stream when modal closes
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const handleFileChange = (e, side) => {
    const file = e.target.files[0];
    if (!file) return;
    if (side === 'front') {
      setFrontImage(file);
      setFrontPreview(URL.createObjectURL(file));
    } else if (side === 'back') {
      setBackImage(file);
      setBackPreview(URL.createObjectURL(file));
    } else if (side === 'selfie') {
      setSelfieImage(file);
      setSelfiePreview(URL.createObjectURL(file));
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
      setShowCamera(true);
    } catch (err) {
      alert('Could not access camera. Please upload a photo instead.');
      console.error(err);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => {
      const file = new File([blob], `selfie_${Date.now()}.png`, { type: 'image/png' });
      setSelfieImage(file);
      setSelfiePreview(URL.createObjectURL(file));
    }, 'image/png');
    stopCamera();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!frontImage || !backImage) {
      setMessage('Please upload both front and back images.');
      return;
    }
    if (!selfieImage) {
      setMessage('Please capture or upload a selfie.');
      return;
    }

    const formData = new FormData();
    formData.append('fullName', form.fullName);
    formData.append('documentType', form.documentType);
    formData.append('documentNumber', form.documentNumber);
    formData.append('frontImage', frontImage);
    formData.append('backImage', backImage);
    formData.append('selfieImage', selfieImage);   // NEW

    try {
      await api.post('/api/kyc/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessage('KYC submitted successfully!');
      // Clear all
      setFrontImage(null); setBackImage(null); setSelfieImage(null);
      setFrontPreview(''); setBackPreview(''); setSelfiePreview('');
      // Refresh status
      const res = await api.get('/api/kyc/status');
      setKyc(res.data);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Submission failed');
    }
  };

  return (
    <div>
      <h2>Verification Center</h2>
      <div className="card" style={{ maxWidth: '600px', marginTop: '1rem' }}>
        <p><strong>Status:</strong> {kyc?.kycStatus || 'Not submitted'}</p>
        {kyc?.details && (
          <div style={{ margin: '1rem 0' }}>
            <p><strong>Name:</strong> {kyc.details.fullName}</p>
            <p><strong>Document:</strong> {kyc.details.documentType} - {kyc.details.documentNumber}</p>
            {kyc.details.frontImage && <img src={kyc.details.frontImage} alt="Front" style={{ width: 150, marginRight: 10 }} />}
            {kyc.details.backImage && <img src={kyc.details.backImage} alt="Back" style={{ width: 150, marginRight: 10 }} />}
            {kyc.details.selfieImage && <img src={kyc.details.selfieImage} alt="Selfie" style={{ width: 150 }} />}
          </div>
        )}

        {kyc?.kycStatus !== 'verified' && (
          <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
            {/* Full Name */}
            <input
              placeholder="Full Legal Name"
              value={form.fullName}
              onChange={e => setForm({ ...form, fullName: e.target.value })}
              required
              style={{ width: '100%', padding: '0.75rem', marginBottom: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
            />
            {/* Document Number */}
            <input
              placeholder="Document Number"
              value={form.documentNumber}
              onChange={e => setForm({ ...form, documentNumber: e.target.value })}
              required
              style={{ width: '100%', padding: '0.75rem', marginBottom: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
            />
            {/* Document Type */}
            <select
              value={form.documentType}
              onChange={e => setForm({ ...form, documentType: e.target.value })}
              style={{ width: '100%', padding: '0.75rem', marginBottom: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
            >
              <option>Passport</option>
              <option>Driver's License</option>
              <option>National ID</option>
            </select>

            {/* Front image */}
            <div style={{ marginBottom: '1rem' }}>
              <label>Front of ID:</label>
              <input type="file" accept="image/*" capture="environment" onChange={e => handleFileChange(e, 'front')} required style={{ display: 'block', marginTop: '0.5rem' }} />
              {frontPreview && <img src={frontPreview} alt="Front preview" style={{ width: 200, height: 'auto', marginTop: '0.5rem', borderRadius: '0.5rem' }} />}
            </div>

            {/* Back image */}
            <div style={{ marginBottom: '1rem' }}>
              <label>Back of ID:</label>
              <input type="file" accept="image/*" capture="environment" onChange={e => handleFileChange(e, 'back')} required style={{ display: 'block', marginTop: '0.5rem' }} />
              {backPreview && <img src={backPreview} alt="Back preview" style={{ width: 200, height: 'auto', marginTop: '0.5rem', borderRadius: '0.5rem' }} />}
            </div>

            {/* Selfie section */}
            <div style={{ marginBottom: '1rem' }}>
              <label>Selfie / Passport Photo:</label>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" onClick={startCamera} style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: 'pointer' }}>
                  📷 Take Selfie
                </button>
                <label style={{ background: '#f1f5f9', padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: 'pointer', border: '1px solid #e2e8f0' }}>
                  📁 Upload Photo
                  <input type="file" accept="image/*" onChange={e => handleFileChange(e, 'selfie')} style={{ display: 'none' }} />
                </label>
              </div>
              {selfiePreview && <img src={selfiePreview} alt="Selfie preview" style={{ width: 200, height: 'auto', marginTop: '0.5rem', borderRadius: '0.5rem', display: 'block' }} />}
            </div>

            {/* Camera Modal */}
            {showCamera && (
              <div style={{
                position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center',
                alignItems: 'center', zIndex: 1000,
              }}>
                <div style={{ background: 'white', padding: '1.5rem', borderRadius: '1rem', textAlign: 'center' }}>
                  <video ref={videoRef} autoPlay playsInline style={{ width: '100%', maxWidth: '400px', borderRadius: '0.5rem' }}></video>
                  <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
                  <div style={{ marginTop: '1rem' }}>
                    <button onClick={capturePhoto} style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.5rem 1.5rem', borderRadius: '0.5rem', marginRight: '0.5rem', cursor: 'pointer' }}>
                      Capture
                    </button>
                    <button onClick={stopCamera} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.5rem 1.5rem', borderRadius: '0.5rem', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {message && (
              <p style={{ color: message.includes('success') ? 'green' : 'red' }}>{message}</p>
            )}

            <button
              type="submit"
              style={{
                background: '#3b82f6', color: 'white', padding: '0.75rem 1.5rem', border: 'none',
                borderRadius: '0.5rem', fontWeight: 500, cursor: 'pointer', marginTop: '1rem',
              }}
            >
              Submit for Verification
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default VerificationCenter;